import React, { useRef, useState } from "react";

import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";

import AudiogramRenderer, {
  getDefaultEditorState,
} from "../../Audiograms/components/templates/AudiogramRenderer";
import {
  AudiogramEditorState,
  AudiogramTemplate,
  BackgroundFit,
  TranscriptParagraph,
  ACCENT_PRESETS,
  FONT_OPTIONS,
  TEMPLATE_OPTIONS,
  WaveformStyle,
} from "../../Audiograms/types";
import { toHHMMSS } from "../../../utils/formatTIme";
import { audiogramsService } from "../../Audiograms/services";
import { EDITOR_TOKENS, SECTION_LABEL_SX } from "../editorTokens";

interface EditorPanelProps {
  state: AudiogramEditorState;
  onChange: (update: Partial<AudiogramEditorState>) => void;
  onParagraphEdit?: (id: string, text: string) => void;
  currentTime?: number;
  audiogramId?: string;
}

function TemplateTab({
  state,
  onChange,
}: {
  state: AudiogramEditorState;
  onChange: (u: Partial<AudiogramEditorState>) => void;
}) {
  const handleSelectTemplate = (template: AudiogramTemplate) => {
    const defaults = getDefaultEditorState(template);
    onChange({
      template,
      accentColor: defaults.accentColor,
      fontFamily: defaults.fontFamily,
      fontSize: defaults.fontSize,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography
        sx={{
          ...SECTION_LABEL_SX,
        }}
      >
        Choose template
      </Typography>
      {TEMPLATE_OPTIONS.map((opt) => (
        <Box
          key={opt.id}
          onClick={() => !opt.disabled && handleSelectTemplate(opt.id)}
          sx={{
            border: "2px solid",
            borderColor: state.template === opt.id ? "#2563eb" : "divider",
            borderRadius: "12px",
            overflow: "hidden",
            cursor: opt.disabled ? "default" : "pointer",
            transition: "border-color 0.15s ease",
            opacity: opt.disabled ? 0.5 : 1,
            pointerEvents: opt.disabled ? "none" : "auto",
            "&:hover": { borderColor: opt.disabled ? undefined : "#2563eb" },
            position: "relative",
          }}
        >
          {opt.disabled && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: "rgba(0,0,0,0.6)",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 600,
                px: 1,
                py: 0.25,
                borderRadius: "4px",
                letterSpacing: "0.04em",
              }}
            >
              Coming soon
            </Box>
          )}
          <Box
            sx={{
              height: 120,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <AudiogramRenderer
              state={{
                ...getDefaultEditorState(opt.id),
                quote: state.quote,
                showName: state.showName,
                guestName: state.guestName,
              }}
              scale={0.45}
            />
          </Box>
          <Box sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
              {opt.label}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
              {opt.description}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function TranscriptParagraphItem({
  paragraph,
  index,
  isSelected,
  isPlaying,
  onSelect,
  onTextChange,
}: {
  paragraph: TranscriptParagraph;
  index: number;
  isSelected: boolean;
  isPlaying: boolean;
  onSelect: (index: number) => void;
  onTextChange: (index: number, text: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const handleClick = () => {
    if (!isEditing) {
      onSelect(index);
    }
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const bgColor = () => {
    if (isEditing) return EDITOR_TOKENS.accentSoft;
    if (isSelected) return "#2563eb";
    if (isPlaying) return "rgba(37, 99, 235, 0.06)";
    return "transparent";
  };

  const textColor = () => {
    if (isEditing) return "#000";
    if (isSelected) return "#fff";
    return "inherit";
  };

  return (
    <Box
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      sx={{
        p: 1.25,
        mb: 0.5,
        backgroundColor: bgColor(),
        borderRadius: "8px",
        border: isEditing
          ? "1px solid rgba(37, 99, 235, 0.3)"
          : "1px solid transparent",
        cursor: isEditing ? "default" : "pointer",
        transition: "all 0.15s ease",
        "&:hover": {
          backgroundColor: (() => {
            if (isEditing) return EDITOR_TOKENS.accentSoft;
            if (isSelected) return "#2563eb";
            return "rgba(37, 99, 235, 0.06)";
          })(),
        },
      }}
    >
      {isEditing ? (
        <TextField
          fullWidth
          multiline
          variant="standard"
          value={paragraph.text || ""}
          onChange={(e) => onTextChange(index, e.target.value)}
          onBlur={() => setIsEditing(false)}
          autoFocus
          sx={{
            "& .MuiInputBase-root": {
              color: "#000",
              padding: 0,
              lineHeight: "1.5",
            },
            "& .MuiInputBase-input": {
              fontSize: "13px",
              lineHeight: "1.5",
              padding: 0,
            },
          }}
        />
      ) : (
        <Typography
          sx={{
            fontSize: "13px",
            lineHeight: "1.5",
            color: textColor(),
            wordWrap: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {paragraph.text || ""}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mt: 0.5,
        }}
      >
        {isPlaying && !isSelected && (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#2563eb",
              flexShrink: 0,
            }}
          />
        )}
        <Typography
          variant="caption"
          sx={{
            fontSize: "10px",
            color: isSelected ? "rgba(255,255,255,0.7)" : "text.secondary",
            letterSpacing: "0.02em",
          }}
        >
          {toHHMMSS(paragraph.start.toString())} —{" "}
          {toHHMMSS(paragraph.end.toString())}
        </Typography>
      </Box>
    </Box>
  );
}

function QuoteTab({
  state,
  onChange,
  onParagraphEdit,
  currentTime,
}: {
  state: AudiogramEditorState;
  onChange: (u: Partial<AudiogramEditorState>) => void;
  onParagraphEdit?: (id: string, text: string) => void;
  currentTime: number;
}) {
  const hasParagraphs = state.transcriptParagraphs.length > 0;

  const handleSelectParagraph = (index: number) => {
    onChange({
      selectedParagraphIndex: index,
      quote: state.transcriptParagraphs[index].text,
    });
  };

  const handleParagraphTextChange = (index: number, text: string) => {
    const paragraph = state.transcriptParagraphs[index];
    const updated = state.transcriptParagraphs.map((p, i) =>
      i === index ? { ...p, text } : p,
    );
    // Persist the edit through the backend's `paragraph` field (it updates the
    // matching transcript paragraph and recomputes word timings). Also select
    // it and mirror into `quote` so the displayed quote stays in sync.
    onChange({
      transcriptParagraphs: updated,
      selectedParagraphIndex: index,
      quote: text,
    });
    if (paragraph) {
      onParagraphEdit?.(paragraph.id, text);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {hasParagraphs ? (
        <Box>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 1,
            }}
          >
            Transcript ({state.transcriptParagraphs.length}{" "}
            {state.transcriptParagraphs.length === 1 ? "segment" : "segments"})
          </Typography>
          <Box
            sx={{
              maxHeight: 300,
              overflow: "auto",
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              p: 0.5,
            }}
          >
            {state.transcriptParagraphs.map((p, i) => (
              <TranscriptParagraphItem
                key={p.id}
                paragraph={p}
                index={i}
                isSelected={i === state.selectedParagraphIndex}
                isPlaying={currentTime >= p.start && currentTime <= p.end}
                onSelect={handleSelectParagraph}
                onTextChange={handleParagraphTextChange}
              />
            ))}
          </Box>
          <Typography
            sx={{
              fontSize: "11px",
              color: "text.secondary",
              mt: 0.5,
            }}
          >
            Select a segment to display on the audiogram
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 1,
            }}
          >
            Text
          </Typography>
          <TextField
            multiline
            minRows={4}
            maxRows={8}
            value={state.quote}
            onChange={(e) => onChange({ quote: e.target.value })}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: "14px",
              },
            }}
          />
        </Box>
      )}

      <Box>
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Font
        </Typography>
        <Select
          value={state.fontFamily}
          onChange={(e) => onChange({ fontFamily: e.target.value })}
          size="small"
          fullWidth
          renderValue={(value) => {
            const font = FONT_OPTIONS.find((f) => f.family === value);
            return (
              <Typography sx={{ fontFamily: value, fontSize: "14px" }}>
                {font?.label ?? value}
              </Typography>
            );
          }}
          sx={{
            borderRadius: "10px",
            fontSize: "14px",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
          }}
        >
          {FONT_OPTIONS.map((font) => (
            <MenuItem key={font.id} value={font.family}>
              <Typography sx={{ fontFamily: font.family, fontSize: "15px" }}>
                {font.label}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box>
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Font size — {state.fontSize}px
        </Typography>
        <Slider
          value={state.fontSize}
          onChange={(_, v) => onChange({ fontSize: v as number })}
          min={12}
          max={28}
          step={1}
          size="small"
          sx={{ color: "#2563eb" }}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Text color
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
          {[
            "#FFFFFF",
            "#E5E7EB",
            "#D1D5DB",
            "#111111",
            "#374151",
            "#6B7280",
          ].map((color) => (
            <Box
              key={color}
              onClick={() => onChange({ textColor: color })}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                backgroundColor: color,
                cursor: "pointer",
                border: "2px solid",
                borderColor: (() => {
                  if (state.textColor === color) return "#2563eb";
                  if (color === "#FFFFFF") return "#E5E7EB";
                  return "transparent";
                })(),
                transition: "border-color 0.15s",
                "&:hover": { transform: "scale(1.1)" },
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              border: "1px solid #0000003b",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": { borderColor: "#000", cursor: "pointer" },
            }}
          >
            <MuiColorInput
              format="hex"
              value={state.textColor || "#FFFFFF"}
              onChange={(color) => onChange({ textColor: color })}
              size="small"
              sx={{
                "& input": { width: 0, border: "none", paddingRight: 0 },
                "& fieldset": { display: "none" },
                "& button": {
                  boxShadow: "none",
                  width: 24,
                  height: 24,
                  marginLeft: "8px",
                },
                "& .MuiInputBase-root": {
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                width: 38,
                height: 38,
              }}
            />
          </Box>
          <TextField
            value={state.textColor}
            onChange={(e) => {
              if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) {
                onChange({ textColor: e.target.value });
              }
            }}
            size="small"
            placeholder="#FFFFFF"
            sx={{
              width: 110,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

function BrandingTab({
  state,
  onChange,
  audiogramId,
}: {
  state: AudiogramEditorState;
  onChange: (u: Partial<AudiogramEditorState>) => void;
  audiogramId?: string;
}) {
  const [customHex, setCustomHex] = useState(state.accentColor);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !audiogramId) return;
    const localUrl = URL.createObjectURL(file);
    onChange({ backgroundImage: localUrl });
    try {
      setUploading(true);
      const key = await audiogramsService.uploadBackgroundImage(
        audiogramId,
        file,
      );
      onChange({ backgroundImageKey: key });
    } catch {
      onChange({ backgroundImage: undefined, backgroundImageKey: undefined });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    onChange({
      backgroundImage: undefined,
      backgroundImageKey: null,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const BG_FIT_OPTIONS: {
    value: BackgroundFit;
    label: string;
  }[] = [
    { value: "cover", label: "Cover" },
    { value: "contain", label: "Contain" },
    { value: "blur", label: "Blur fill" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Show name
        </Typography>
        <TextField
          value={state.showName}
          onChange={(e) => onChange({ showName: e.target.value })}
          fullWidth
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "14px",
            },
          }}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Guest name
        </Typography>
        <TextField
          value={state.guestName}
          onChange={(e) => onChange({ guestName: e.target.value })}
          fullWidth
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "14px",
            },
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={state.showGuestAvatar}
              onChange={(e) => onChange({ showGuestAvatar: e.target.checked })}
              size="small"
            />
          }
          label={
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              Show initials avatar
            </Typography>
          }
          sx={{ mt: 1, ml: 0, pl: 0 }}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Accent color
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
          {ACCENT_PRESETS.map((color) => (
            <Box
              key={color}
              onClick={() => {
                onChange({ accentColor: color });
                setCustomHex(color);
              }}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                backgroundColor: color,
                cursor: "pointer",
                border: "2px solid",
                borderColor:
                  state.accentColor === color ? "#111" : "transparent",
                transition: "border-color 0.15s",
                "&:hover": { transform: "scale(1.1)" },
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              border: "1px solid #0000003b",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": { borderColor: "#000", cursor: "pointer" },
            }}
          >
            <MuiColorInput
              format="hex"
              value={state.accentColor || "#000000"}
              onChange={(color) => {
                onChange({ accentColor: color });
                setCustomHex(color);
              }}
              size="small"
              sx={{
                "& input": { width: 0, border: "none", paddingRight: 0 },
                "& fieldset": { display: "none" },
                "& button": {
                  boxShadow: "none",
                  width: 24,
                  height: 24,
                  marginLeft: "8px",
                },
                "& .MuiInputBase-root": {
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                width: 38,
                height: 38,
              }}
            />
          </Box>
          <TextField
            value={customHex}
            onChange={(e) => {
              setCustomHex(e.target.value);
              if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                onChange({ accentColor: e.target.value });
              }
            }}
            size="small"
            placeholder="#000000"
            sx={{
              width: 110,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
              },
            }}
          />
        </Box>
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={state.showDate}
              onChange={(e) => onChange({ showDate: e.target.checked })}
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#2563eb",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#2563eb",
                },
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              Show date
            </Typography>
          }
          sx={{ ml: 0 }}
        />
      </Box>

      {/* Background Image */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          pt: 2.5,
        }}
      >
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Background image
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: "none" }}
        />

        {state.backgroundImage ? (
          <Box>
            <Box
              sx={{
                width: "100%",
                height: 100,
                borderRadius: "10px",
                overflow: "hidden",
                mb: 1,
                position: "relative",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                component="img"
                src={state.backgroundImage}
                alt="Background"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                sx={{
                  flex: 1,
                  fontSize: "11px",
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "divider",
                  color: "text.primary",
                }}
              >
                {uploading ? "Uploading…" : "Replace"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleRemoveImage}
                disabled={uploading}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                sx={{
                  flex: 1,
                  fontSize: "11px",
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#fca5a5",
                  color: EDITOR_TOKENS.danger,
                  "&:hover": {
                    borderColor: EDITOR_TOKENS.danger,
                    backgroundColor: "rgba(239,68,68,0.04)",
                  },
                }}
              >
                Remove
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: "2px dashed",
              borderColor: "divider",
              borderRadius: "10px",
              p: 2.5,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: "#2563eb",
                backgroundColor: "rgba(37,99,235,0.02)",
              },
            }}
          >
            <ImageIcon
              sx={{
                fontSize: 28,
                color: "text.secondary",
                mb: 0.5,
              }}
            />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              Upload background
            </Typography>
            <Typography
              sx={{
                fontSize: "10px",
                color: "text.secondary",
                mt: 0.25,
              }}
            >
              JPG, PNG or WebP
            </Typography>
          </Box>
        )}

        {state.backgroundImage && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 1,
              }}
            >
              Overlay opacity — {Math.round(state.backgroundOverlay * 100)}%
            </Typography>
            <Slider
              value={state.backgroundOverlay}
              onChange={(_, v) =>
                onChange({
                  backgroundOverlay: v as number,
                })
              }
              min={0}
              max={0.8}
              step={0.05}
              size="small"
              sx={{ color: "#2563eb" }}
            />

            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 1,
                mt: 1.5,
              }}
            >
              Background fit
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {BG_FIT_OPTIONS.map((opt) => (
                <Box
                  key={opt.value}
                  onClick={() => onChange({ backgroundFit: opt.value })}
                  sx={{
                    flex: 1,
                    border: "1.5px solid",
                    borderColor:
                      state.backgroundFit === opt.value ? "#2563eb" : "divider",
                    borderRadius: "8px",
                    p: 1,
                    cursor: "pointer",
                    textAlign: "center",
                    backgroundColor:
                      state.backgroundFit === opt.value
                        ? EDITOR_TOKENS.accentSofter
                        : "transparent",
                    transition: "all 0.15s ease",
                    "&:hover": { borderColor: "#2563eb" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {opt.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function WaveformTab({
  state,
  onChange,
}: {
  state: AudiogramEditorState;
  onChange: (u: Partial<AudiogramEditorState>) => void;
}) {
  const WAVEFORM_STYLES: { value: WaveformStyle; label: string }[] = [
    { value: "bars", label: "Bars" },
    { value: "mirrored", label: "Mirrored" },
    { value: "dots", label: "Dots" },
    { value: "line", label: "Line" },
  ];

  const HEIGHT_OPTIONS: {
    value: "compact" | "medium" | "tall";
    label: string;
  }[] = [
    { value: "compact", label: "Compact" },
    { value: "medium", label: "Medium" },
    { value: "tall", label: "Tall" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Waveform style
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {WAVEFORM_STYLES.map((ws) => (
            <Box
              key={ws.value}
              onClick={() => onChange({ waveformStyle: ws.value })}
              sx={{
                flex: 1,
                border: "1.5px solid",
                borderColor:
                  state.waveformStyle === ws.value ? "#2563eb" : "divider",
                borderRadius: "8px",
                p: 1,
                cursor: "pointer",
                textAlign: "center",
                backgroundColor:
                  state.waveformStyle === ws.value
                    ? EDITOR_TOKENS.accentSofter
                    : "transparent",
                transition: "all 0.15s ease",
                "&:hover": { borderColor: "#2563eb" },
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                {ws.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{
            ...SECTION_LABEL_SX,
            mb: 1,
          }}
        >
          Height
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {HEIGHT_OPTIONS.map((h) => (
            <Box
              key={h.value}
              onClick={() => onChange({ waveformHeight: h.value })}
              sx={{
                flex: 1,
                border: "1.5px solid",
                borderColor:
                  state.waveformHeight === h.value ? "#2563eb" : "divider",
                borderRadius: "8px",
                p: 1,
                cursor: "pointer",
                textAlign: "center",
                backgroundColor:
                  state.waveformHeight === h.value
                    ? EDITOR_TOKENS.accentSofter
                    : "transparent",
                transition: "all 0.15s ease",
                "&:hover": { borderColor: "#2563eb" },
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                {h.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={state.showWaveform}
              onChange={(e) => onChange({ showWaveform: e.target.checked })}
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#2563eb" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#2563eb",
                },
              }}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                Show waveform
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                {state.showWaveform
                  ? "Waveform visible in export"
                  : "No waveform in export"}
              </Typography>
            </Box>
          }
        />
      </Box>
    </Box>
  );
}

export default function EditorPanel({
  state,
  onChange,
  onParagraphEdit,
  currentTime = 0,
  audiogramId,
}: EditorPanelProps) {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 44,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: EDITOR_TOKENS.panelSurface,
          "& .MuiTab-root": {
            fontSize: "12px",
            fontWeight: 600,
            textTransform: "none",
            minHeight: 44,
            py: 0,
            color: "text.secondary",
            transition: "color 0.15s ease, background-color 0.15s ease",
            "&:hover": {
              color: "text.primary",
              backgroundColor: EDITOR_TOKENS.accentSofter,
            },
            "&:focus-visible": {
              outline: `2px solid ${EDITOR_TOKENS.accent}`,
              outlineOffset: "-2px",
            },
          },
          "& .Mui-selected": { color: EDITOR_TOKENS.accent },
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "3px 3px 0 0",
            backgroundColor: EDITOR_TOKENS.accent,
          },
        }}
      >
        <Tab label="Template" />
        <Tab label="Text" />
        <Tab label="Branding" />
        <Tab label="Waveform" />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2.5,
        }}
      >
        {activeTab === 0 && <TemplateTab state={state} onChange={onChange} />}
        {activeTab === 1 && (
          <QuoteTab
            state={state}
            onChange={onChange}
            onParagraphEdit={onParagraphEdit}
            currentTime={currentTime}
          />
        )}
        {activeTab === 2 && (
          <BrandingTab
            state={state}
            onChange={onChange}
            audiogramId={audiogramId}
          />
        )}
        {activeTab === 3 && <WaveformTab state={state} onChange={onChange} />}
      </Box>
    </Box>
  );
}
